import { format } from 'date-fns/format'
import { parseISO } from 'date-fns/parseISO'
import {
  getItineraryType,
  getNumOfPassengers,
} from '../../../listings-client/services/airSearch/airSearchRequest/helpers'

import { getPackageListingsUrlFromParams, getSelectedFareBrands } from '../../helpers/helpers'

export function buildCheckoutObject({
  isInterline,
  isFused,
  airlines,
  airports,
  equipment,
  isBEC,
  fareBrandsInfo,
  tripType,
  isExpressDeal = false,
  flightString,
  searchRequest,
  itemKey,
  priceKey,
  price,
  defaultBrandsByKeys,
  selectionsBySliceId,
  itinerarySlices: slices,
  packageSpecificPriceNodes,
  fromPackages,
  offerToken,
  passengers: pax,
  pricingPerPaxType,
  flyBundleComponent,
  isPackageListings,
  proposalComponents,
}) {
  const { isFareBrandUpgrade, selectedFareBrands } = getSelectedFareBrands({
    defaultBrandsByKeys,
    selectionsBySliceId,
    fareBrandsInfo,
  })
  const totalPrice = price?.find((priceNode) => priceNode.type === 'TOTAL_PRICE')
  const pricePerPax = price?.find((priceNode) => priceNode.type === 'AVG_PRICE')
  const originalTicketCost = price?.find((priceNode) => priceNode.type === 'ADULT')?.amount

  const sliceLength = slices.length
  const itinDepart = slices[0].departInfo
  const itinArrival =
    tripType === 'RT' ? slices[sliceLength - 1].departInfo : slices[sliceLength - 1].arrivalInfo

  const { params } = searchRequest
  const { 'cart-key': cartKey, 'usp-cache-key': uspCacheKey } = params

  const componentKey = params['component-key'] || flyBundleComponent?.key
  const componentIndex = Number(params['component-index'] || flyBundleComponent?.index)

  let passengers = {
    adults: getNumOfPassengers(params['num-adults']) || 1,
    children: getNumOfPassengers(params['num-children']),
    infants: getNumOfPassengers(params['num-infants']),
  }

  if (pax) {
    const { ADULT, CHILD, INFANT, LAP_INFANT, SEAT_INFANT } = pax

    const isInfantsParamGreater =
      getNumOfPassengers(params['num-infants']) > INFANT + LAP_INFANT + SEAT_INFANT

    const infantsValue = isInfantsParamGreater
      ? getNumOfPassengers(params['num-infants'])
      : INFANT + LAP_INFANT + SEAT_INFANT

    passengers = {
      adults: ADULT,
      children: CHILD,
      infants: infantsValue,
    }

    if (isInfantsParamGreater) pax.LAP_INFANT = passengers.infants
  }

  const travelers = getTravelersNode({
    params,
    passengers: pax,
    fromPackages,
  })

  const originAirport = itinDepart.airport
  const destinationAirport = itinArrival.airport

  const departTimeInfo = itinDepart.time
  const arrivalTimeInfo = itinArrival.time

  const origin = {
    id: originAirport.code,
    name: originAirport.name,
    type: 'AIRPORT',
    isoCountryCode: originAirport.isoCountryCode,
    displayName: originAirport.displayLocation,
  }

  const destination = {
    id: destinationAirport.code,
    name: destinationAirport.name,
    type: 'AIRPORT',
    isoCountryCode: destinationAirport.isoCountryCode,
    displayName: destinationAirport.displayLocation,
  }

  let searchUrl = getPackageListingsUrlFromParams({ params, uspCacheKey, cartKey, offerToken })

  const flyComponent = {
    itineraryType: getItineraryType(tripType),
    productId: 1,
    type: 'FLY',
    // url to go back to flights path,
    searchUrl,
    flightSelected: true,
    meta: getMeta({
      isExpressDeal,
      flightString,
      isInterline,
      isFused,
      rawSlices: slices,
      airlines: convertArrayToObj(airlines),
      airports: convertArrayToObj(airports),
      equipment: convertArrayToObj(equipment),
      pricePerPax,
      isBEC,
      passengers,
    }),
    isFareBrandUpgrade,
  }

  if (componentKey) {
    flyComponent.componentKey = componentKey
  }
  if (componentIndex) {
    flyComponent.index = componentIndex
  }

  const checkOutObj = {
    unifiedBasketInfo: {
      itemType: 'FLY_RETAIL',
      itemPriceReference: { itemKey, priceKey },
    },
    isFireFlyPackage: !!fromPackages,
    travelers,
    travelStartDate: format(parseISO(departTimeInfo.dateTime), 'MM/dd/yyyy'),
    travelEndDate: format(parseISO(arrivalTimeInfo.dateTime), 'MM/dd/yyyy'),
    origin,
    destination,
    components: [flyComponent],
    sourceUrl: 'https://www.priceline.com/flight/',
    selectedFareBrands,
    userActionType: params?.['userActionType'] === 'MODIFY' ? 'MODIFY' : false,
  }

  /**
   * Don't move inside checkoutObj as Packages receives cartKey as 'undefined' string
   * */
  if (cartKey) {
    checkOutObj.cartKey = cartKey
  }
  if (uspCacheKey) {
    checkOutObj.uspCacheKey = uspCacheKey
  }

  if (window?.PCLN_BOOTSTRAP_DATA?.workflowId) {
    checkOutObj.unifiedBasketInfo.workflowId = window?.PCLN_BOOTSTRAP_DATA?.workflowId
  }

  if (offerToken) {
    checkOutObj.unifiedBasketInfo.flyItemContext = {
      flyPostBookingContext: {
        bookingReference: {
          offerToken: offerToken,
        },
      },
    }
  }
  if (pax?.LAP_INFANT) {
    checkOutObj.unifiedBasketInfo.flyItemContext = {
      lapInfantCount: pax?.LAP_INFANT,
    }
    checkOutObj.components[0].flyItemContext = {
      lapInfantCount: pax?.LAP_INFANT,
    }
  }

  if (isPackageListings) {
    checkOutObj.proposalComponents = proposalComponents
    checkOutObj.pricing = totalPrice?.packagesPriceData
    return checkOutObj
  }

  // isUspRequestCache boolean is only needed for details page
  checkOutObj.isUspRequestCache = Boolean(uspCacheKey)

  /**
   * Don't send pricing info if it is a package flight and user did not change upsell(packageSpecificPriceNodes is null)
   * */
  const shouldSendPricingObject = !fromPackages || packageSpecificPriceNodes

  if (shouldSendPricingObject) {
    checkOutObj.pricing = getUCPricingObject({
      totalPrice,
      pricePerPax,
      searchRequest,
      packageSpecificPriceNodes,
      fromPackages,
      priceKey,
      offerToken,
      originalTicketCost,
      pricingPerPaxType,
      isPackageListings,
    })
  }

  return checkOutObj
}

export function getMeta({
  isExpressDeal,
  flightString,
  isInterline,
  isFused,
  rawSlices,
  airlines,
  airports,
  equipment,
  pricePerPax,
  isBEC,
  passengers,
}) {
  const { adults, children, infants } = passengers
  const currencySymbol = '$'
  const { amount: paxAmount } = pricePerPax
  const roundedPrice = Math.ceil(paxAmount)
  // eslint-disable-next-line sonarjs/prefer-immediate-return
  const meta = {
    opaque: isExpressDeal,
    retail: !isExpressDeal,
    basicEconomy: isBEC,
    requiresDisinsection: null, // Confirm with packages
    isFreeCancellation: false,
    isHackerFare: isInterline || isFused,
    flightString,
    slices: getCheckoutSlices({ rawSlices }),
    airlines,
    airports,
    equipment,
    numAdults: adults, // => Passengers Duplicate
    numChildren: children, // => Passengers Duplicate
    numInfants: infants, // => Passengers Duplicate
    retailPrice: {
      // => Duplicate node
      pricePerPerson: roundedPrice,
      displayPricePerPerson: `${currencySymbol}${roundedPrice}`,
    },
    // "itineraryReference": -> Not for FF
    // cabinRestrictions: itinDetails?.cabinRestrictions || {},
    // "baggageFeesUrl": "",
    // "ticketType": "",
    // "ticketingAirline": "",
    // "fareFilingType": "", -> Empty
  }

  return meta
}

// Reevaluate the logic
/**
 * For now all passengers are treated as Adults
 * */
function getUCPricingObject({
  totalPrice,
  pricePerPax,
  searchRequest,
  packageSpecificPriceNodes = {},
  fromPackages,
  offerToken,
  originalTicketCost,
  pricingPerPaxType,
  isPackageListings,
}) {
  const { amount, currencyCode, creditSummary } = totalPrice
  const { subtotalPrice, priceAndFarePerPaxType } = pricingPerPaxType
  const { amount: paxAmount } = pricePerPax

  let totalCost = amount
  let totalCostPerTraveler = paxAmount
  let totalPayLater = 0
  let totalPayNow = amount
  let originalCostPerTraveler = paxAmount
  let totalCostWithHotelMandatoryFees

  let perPersonStrikeThroughPrice
  let perPersonSavings
  let totalSavings = 0
  let potentialSavings = 0
  let totalStrikethrough
  let shouldHidePriceBreakdown = false

  let totalHotelMandatoryFees
  let roomMandatoryFees

  if (fromPackages && !isPackageListings) {
    const {
      savings,
      totalCostWithHotelMandatoryFees: pkgTotalCostWithHotelMandatoryFees,
      totalPackagePrice,
      totalUpsellDisplayPrice,
      totalPayLater: pkgTotalPayLater,
      totalHotelMandatoryFees: pkgTotalHotelMandatoryFees,
      roomMandatoryFees: pkgRoomMandatoryFees,
    } = packageSpecificPriceNodes

    totalCost = totalPackagePrice?.amount
    totalCostPerTraveler = totalUpsellDisplayPrice?.amount
    totalCostWithHotelMandatoryFees = pkgTotalCostWithHotelMandatoryFees?.amount
    totalPayNow = totalPackagePrice?.amount - pkgTotalPayLater?.amount
    perPersonStrikeThroughPrice = savings?.perPersonStrikeThroughPrice
    perPersonSavings = savings?.perPersonSavings
    totalSavings = savings?.totalSavings
    totalStrikethrough = savings?.totalStrikeThroughPrice
    totalPayLater = pkgTotalPayLater?.amount
    totalHotelMandatoryFees = pkgTotalHotelMandatoryFees?.amount
    roomMandatoryFees = pkgRoomMandatoryFees?.amount
    shouldHidePriceBreakdown = true // true because fareBrandUpgraded
  }

  // eslint-disable-next-line sonarjs/prefer-immediate-return
  const pricing = {
    subtotalPrice,
    priceAndFarePerPaxType,
    totalCost,
    totalCostPerTraveler,
    totalPayLater,
    totalPayNow,
    originalCostPerTraveler,
    totalSavings,
    potentialSavings,
    totalCostWithHotelMandatoryFees,
    perPersonStrikeThroughPrice,
    perPersonSavings,
    totalHotelMandatoryFees,
    roomMandatoryFees,
    shouldHidePriceBreakdown,
    currency: currencyCode,
    totalStrikethrough,
    symbol: '$',
    totalTravelers: getNumTravelers({ searchRequest }), //TODO: Reevaluate the logic
    creditSummary: !fromPackages && offerToken ? { ...creditSummary, originalTicketCost } : undefined,
  }

  return pricing
}

function getCheckoutSlices({ rawSlices }) {
  const slices = []

  rawSlices.forEach((slice) => {
    const {
      id,
      duration,
      durationInSeconds,
      durationInMinutes,
      segments: rawSegments,
      stops,
      isOvernight,
    } = slice

    const segments = getCheckoutSegments({ rawSegments })

    const ucSlice = {
      id,
      sliceId: id, // Duplicate -> id
      _metaSliceId: id, // Duplicate -> id
      duration: Number(durationInMinutes) || getDurationInMinutes({ durationInSeconds }),
      formattedDuration: duration,
      numStops: stops,
      nonstop: stops === 0,
      overnight: isOvernight,
      segments,
      layoverLocations: getLayover({ slice, stops }), // Passing layoverInfo from API makes more sense
    }

    slices.push(ucSlice)
  })

  return slices
}

function getCheckoutSegments({ rawSegments }) {
  const segments = []
  rawSegments.forEach((segment) => {
    const {
      id,
      cabinName,
      cabinClass,
      flightNumber,
      equipment,
      stopQuantity,
      isOvernight,
      marketingAirline,
      operatingAirline,
      durationInSeconds,
      duration,
      seatsAvailable,
      departInfo,
      arrivalInfo,
    } = segment

    const cabinNameMap = {
      ECO: 'Economy',
      PEC: 'Premium Economy',
      BUS: 'Business',
      FST: 'First',
    }

    const { airport: originAirport, time: originTime } = departInfo
    const { airport: destinationAirport, time: destinationTime } = arrivalInfo
    const item = {
      id,
      _metaSegmentId: id, // Duplicate -> id
      origAirport: originAirport?.code,
      destAirport: destinationAirport?.code,
      originAirport: originAirport?.code, // Duplicate -> origAirport
      destinationAirport: destinationAirport?.code, // Duplicate => destAirport
      departTime: originTime?.displayTime,
      departDateTime: originTime?.dateTime,
      arrivalDateTime: destinationTime?.dateTime,
      arrivalTime: destinationTime?.displayTime,
      cabinClass,
      cabinName: cabinName || cabinNameMap[cabinClass],
      marketingAirline,
      operatingAirline,
      stopQuantity,
      duration: duration || getDurationInMinutes({ durationInSeconds }),
      equipmentCode: equipment,
      flightNumber,
      seatsAvailable,
      numStops: stopQuantity, // Duplicate => stopQuantity
      overnightFlight: isOvernight,
      overnight: isOvernight, // Duplicate => overnight
      // "bkgClass": "Z",
      // "genericSeatAssgnFlag": false,
      // "genericSeatAssgnCost": null
      // "premiumSeatingFlag": false,
      // "seatmapAvailable": false,
      // "seatSelectionAllowed": false,
      // "allowStandby": false,
      // "allowCabinUpgrades": false,
      // "allowPreferredSeating": false,
      // "allowPriorityBoarding": false,
      // "distance": 954, -> TODO: Need to check
    }

    segments.push(item)
  })

  return segments
}

function getDurationInMinutes({ durationInSeconds }) {
  return Number(durationInSeconds) / 60
}

function getLayover({ slice, stops }) {
  const layOvers = slice.segments.map((eachSegment) => {
    return eachSegment.destAirport || eachSegment.arrivalInfo?.airport?.code
  })
  return layOvers.splice(0, stops)
}

function getNumTravelers({ searchRequest }) {
  if (!searchRequest?.params) return
  const { params } = searchRequest

  let numberOfPassengers = 0
  for (const node in searchRequest.params) {
    if (node === 'num-adults' || node === 'num-children') {
      numberOfPassengers += parseInt(params[node])
    }
  }
  return numberOfPassengers
}

export function convertArrayToObj(arrayNode = []) {
  const obj = {}

  arrayNode.forEach((arrayItem) => {
    obj[arrayItem.code] = arrayItem
  })

  return obj
}

function getTravelersNode({ params, passengers, fromPackages }) {
  const passengerTypes = ['num-adults', 'num-children', 'num-infants']
  const passengerTypeAge = {
    'num-children': 14,
    'num-infants': 1,
  }

  const travelers = {
    adults: 0,
    children: [],
  }

  if (fromPackages || !passengers) {
    passengerTypes.forEach((passengerType) => {
      const passengerCount = Number(params[passengerType])
      if (!passengerCount) {
        return
      }

      if (passengerType === 'num-adults') {
        travelers.adults = passengerCount
      } else {
        for (let count = 0; count < passengerCount; count++) {
          travelers.children.push({
            age: passengerTypeAge[passengerType],
            lapChild: passengerType === 'num-infants',
          })
        }
      }
    })
  } else {
    const { ADULT, CHILD, INFANT, LAP_INFANT, SEAT_INFANT } = passengers
    travelers.adults = ADULT

    const totalInfant = INFANT + SEAT_INFANT

    for (let num = 0; num < totalInfant; num++) {
      travelers.children.push({
        age: 1,
        lapChild: false,
      })
    }

    for (let num = 0; num < LAP_INFANT; num++) {
      travelers.children.push({
        age: 1,
        lapChild: true,
      })
    }

    for (let num = 0; num < CHILD; num++) {
      travelers.children.push({
        age: 14,
        lapChild: false,
      })
    }
  }

  return travelers
}
